import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { AuthService } from '@core/service/auth.service';
import { RestClientService } from '@core/service/rest-client.service';
import { PrescriptionHubComponent } from '@shared/components/prescription-hub/prescription-hub.component';

interface GarantieCol {
  codif: string;
  label: string;
  countKey: string;
  activiteCode: string;
  isOrdonnance?: boolean;
}

const GARANTIE_COLS: GarantieCol[] = [
  {
    codif: 'PHARMA',
    label: 'Ordonnance',
    countKey: 'count_pharma_non_hospit',
    activiteCode: 'ORDONNANCE',
    isOrdonnance: true,
  },
  {
    codif: 'BIO',
    label: 'Biologie',
    countKey: 'count_bio_non_hospit',
    activiteCode: 'BIO',
  },
  {
    codif: 'IMA',
    label: 'Imagerie',
    countKey: 'count_ima_non_hospit',
    activiteCode: 'IMA',
  },
  {
    codif: 'OPT',
    label: 'Optique',
    countKey: 'count_opt_non_hospit',
    activiteCode: 'OPT',
  },
  {
    codif: 'DEN',
    label: 'Dentisterie',
    countKey: 'count_den_non_hospit',
    activiteCode: 'DEN',
  },
  {
    codif: 'MAT',
    label: 'Maternité',
    countKey: 'count_mat_non_hospit',
    activiteCode: 'MAT',
  },
  {
    codif: 'AUX',
    label: 'Auxiliaires',
    countKey: 'count_aux_non_hospit',
    activiteCode: 'AUX',
  },
  {
    codif: 'EXA',
    label: 'Autres exam.',
    countKey: 'count_exa_non_hospit',
    activiteCode: 'EXA',
  },
  {
    codif: 'BILN',
    label: 'Bilan santé',
    countKey: 'count_biln_non_hospit',
    activiteCode: 'BILN',
  },
  {
    codif: 'TRA',
    label: 'Transport',
    countKey: 'count_tra_non_hospit',
    activiteCode: 'TRA',
  },
];

@Component({
  selector: 'app-historique-consultation',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatPaginatorModule,
    PrescriptionHubComponent,
  ],
  templateUrl: './historique-consultation.component.html',
  styleUrl: './historique-consultation.component.scss',
})
export class HistoriqueConsultationComponent implements OnInit {
  GARANTIE_COLS: GarantieCol[] = [];

  user: any = {};
  items: any[] = [];
  loadingList = false;
  totalItems = 0;
  index = 0;
  size = 10;
  dateDebut = '';
  dateFin = '';
  searchKey = '';

  itemsGarantie: any[] = [];
  consGarantieId: number | null = null;

  // ── Hub prescriptions ──
  showHubModal = false;
  hubConsultation: any = {};
  hubBeneficiaire: any = {};

  // ── Liste prescriptions ──
  showListeModal = false;
  listeModalTitle = '';
  listeModalCol: GarantieCol | null = null;
  listeModalItems: any[] = [];
  loadingListe = false;

  // ── Consultations optiques ──
  showConsOptModal = false;
  consOptModalTitle = '';
  consOptItems: any[] = [];
  loadingConsOpt = false;

  // ── Toggle détail ordonnance ──
  expandedOrdonnanceIds = new Set<number>();
  ordonnanceDetails: {
    [id: number]: { prescriptions: any[]; executions: any[] };
  } = {};
  loadingDetailId: number | null = null;

  // ── Comptage ordonnances par consultation × garantie ──
  ordCounts: { [key: string]: number } = {}; // key = `${itemId}_${codif}`

  constructor(
    private restClient: RestClientService,
    private authService: AuthService,
  ) {
    this.user = this.authService.getCurrentUser();
    const today = new Date();
    const first = new Date(today.getFullYear(), today.getMonth(), 1);
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    this.dateDebut = this.toInputDate(first);
    this.dateFin = this.toInputDate(tomorrow);
  }

  ngOnInit() {
    this.buildVisibleCols();
    this.getItemsGarantie();
  }

  buildVisibleCols() {
    const userActivites = [
      ...(this.user.activite_utilisateur ?? []),
      ...(this.user.activite_utilisateur_autre ?? []),
    ].map((a: any) => a?.codification?.trim().toLowerCase());

    this.GARANTIE_COLS = GARANTIE_COLS.filter((col) =>
      userActivites.includes(col.activiteCode.toLowerCase()),
    );
  }

  toInputDate(d: Date) {
    return d.toISOString().split('T')[0];
  }

  getItemsGarantie() {
    this.restClient
      .post('garantie/getByCriteria', { index: 0, size: 1000, data: {} })
      .subscribe((res: any) => {
        if (res && res['items']) {
          this.itemsGarantie = res['items'];
          const cons = this.itemsGarantie.find(
            (g: any) => g.codification === 'CONS',
          );
          this.consGarantieId = cons?.id ?? null;
          this.getItems();
        }
      });
  }

  getItems() {
    if (!this.consGarantieId) return;
    this.loadingList = true;
    this.items = [];
    const request: any = {
      index: this.index,
      size: this.size,
      date_debut: this.dateDebut,
      date_fin: this.dateFin,
      data: {
        prestataire_id: this.user.prestataire_id,
        garantie_id: this.consGarantieId,
      },
    };
    if (this.searchKey) request['search_key'] = this.searchKey;
    this.restClient.post('prestationActe/getByCriteria', request).subscribe(
      (res: any) => {
        this.loadingList = false;
        if (res && res['items']) {
          this.items = res['items'];
          this.totalItems = res['count'] ?? this.items.length;
          this.loadOrdCounts();
        } else {
          this.items = [];
        }
      },
      () => {
        this.loadingList = false;
        this.items = [];
      },
    );
  }

  loadOrdCounts() {
    this.ordCounts = {};
    this.GARANTIE_COLS.forEach((col) => {
      const itemsWithActe = this.items.filter(
        (i) => (i[col.countKey] ?? 0) > 0,
      );
      itemsWithActe.forEach((item) => {
        const key = `${item.id}_${col.codif}`;
        this.restClient
          .post('ordonnance/getByCriteria', {
            index: 0,
            size: 1000,
            garantie_codification: col.codif,
            data: {
              beneficiaire_id: item.beneficiaire_id,
              prestataire_id: this.user.prestataire_id,
            },
          })
          .subscribe((res: any) => {
            this.ordCounts[key] = res?.count ?? res?.items?.length ?? 0;
          });
      });
    });
  }

  onSearch() {
    this.index = 0;
    this.getItems();
  }

  onPageChange(e: PageEvent) {
    this.index = e.pageIndex;
    this.size = e.pageSize;
    this.getItems();
  }

  getTotalCount(item: any): number {
    return this.GARANTIE_COLS.reduce(
      (sum, col) => sum + (item[col.countKey] ?? 0),
      0,
    );
  }

  hasAnyActe(item: any): boolean {
    return this.GARANTIE_COLS.some((col) => (item[col.countKey] ?? 0) > 0);
  }

  ouvrirHub(item: any) {
    if (item.statut !== 'ATTENTE_PREVALIDATION_PRESTATAIRE') return;
    this.hubConsultation = item;
    this.hubBeneficiaire = {
      prenom: item.beneficiaire_prenom || '',
      nom: item.beneficiaire_nom || item.nom_beneficiaire || '',
      matricule: item.matricule,
    };
    this.showHubModal = true;
  }

  fermerHub() {
    this.showHubModal = false;
    this.getItems();
  }

  ouvrirListePrescrire(item: any, col: GarantieCol) {
    this.listeModalCol = col;
    this.listeModalTitle = `${col.label} — ${item.matricule} (${item.beneficiaire_nom || item.nom_beneficiaire || ''})`;
    this.listeModalItems = [];
    this.loadingListe = true;
    this.showListeModal = true;
    this.expandedOrdonnanceIds.clear();
    this.ordonnanceDetails = {};

    const request = {
      index: 0,
      size: 1000,
      garantie_codification: col.codif,
      data: {
        beneficiaire_id: item.beneficiaire_id,
        prestataire_id: this.user.prestataire_id,
      },
    };
    this.restClient.post('ordonnance/getByCriteria', request).subscribe(
      (res: any) => {
        this.loadingListe = false;
        this.listeModalItems = res?.items ?? [];
      },
      () => {
        this.loadingListe = false;
      },
    );
  }

  toggleOrdonnanceDetail(ord: any) {
    const id = ord.id;
    if (this.expandedOrdonnanceIds.has(id)) {
      this.expandedOrdonnanceIds.delete(id);
      return;
    }
    this.expandedOrdonnanceIds.add(id);
    if (this.ordonnanceDetails[id]) return; // déjà chargé
    this.loadingDetailId = id;

    // Charger prescription lines et exécutions en parallèle
    let prescriptions: any[] = [];
    let executions: any[] = [];
    let done = 0;

    const merge = () => {
      this.loadingDetailId = null;
      this.ordonnanceDetails[id] = { prescriptions, executions };
    };

    this.restClient
      .post('prescriptionActe/getByCriteria', {
        index: 0,
        size: 1000,
        data: { ordonnance_id: id },
      })
      .subscribe(
        (res: any) => {
          prescriptions = res?.items ?? [];
          if (++done === 2) merge();
        },
        () => {
          prescriptions = [];
          if (++done === 2) merge();
        },
      );

    this.restClient
      .post('prestationActe/getByCriteria', {
        index: 0,
        size: 1000,
        data: { ordonnance_id: id },
      })
      .subscribe(
        (res: any) => {
          executions = res?.items ?? [];
          if (++done === 2) merge();
        },
        () => {
          executions = [];
          if (++done === 2) merge();
        },
      );
  }

  fermerListeModal() {
    this.showListeModal = false;
  }

  ouvrirConsOptModal(item: any) {
    this.consOptModalTitle = `Consultations optiques — ${item.matricule} (${item.beneficiaire_nom || ''})`;
    this.consOptItems = [];
    this.loadingConsOpt = true;
    this.showConsOptModal = true;
    this.restClient.post('consultationOptique/getByCriteria', {
      index: 0,
      size: 1000,
      data: { prestation_acte_id: item.id, beneficiaire_id: item.beneficiaire_id },
    }).subscribe(
      (res: any) => {
        this.loadingConsOpt = false;
        this.consOptItems = res?.items ?? [];
      },
      () => { this.loadingConsOpt = false; }
    );
  }

  fermerConsOptModal() {
    this.showConsOptModal = false;
  }
}
